import React, { useState, useMemo, useEffect } from 'react';
import { Button, message } from 'antd';
import { EnumFields } from 'types/enumerationData';
import { ServerMeta } from 'types/common';
import useGetSettingData from 'hooks/useGetSettingData';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { OrgCredentialingPayerRef, TCredentialingProvider } from 'types/credentialing';
import { saveGroupCredentialingEnrollmentPayers, getCredentialingProviderStates } from 'api/credentialing';
import PayerPickerModal from '../PayerPickerModal';
import s from './s.module.less';

interface PracticePayersCardProps {
    roster: TCredentialingProvider[];
    initialPayers?: OrgCredentialingPayerRef[];
    // 已选 practice payers 变化时上报（Set up your practice 据此决定要不要问 Medicaid group enrollment）
    onPayersChange?: (payers: OrgCredentialingPayerRef[]) => void;
}

// Group payers by state code and convert to display state name.
// Preserves selection order within each state and the order in which states first appear.
const groupByState = (
    payers: OrgCredentialingPayerRef[],
    stateMap: Record<string, string>,
): Array<{ state: string; stateName: string; names: string[] }> => {
    const order: string[] = [];
    const byState: Record<string, string[]> = {};
    payers.forEach((payer) => {
        const stateCode = payer.state;
        if (!byState[stateCode]) {
            byState[stateCode] = [];
            order.push(stateCode);
        }
        byState[stateCode].push(payer.payerName);
    });
    return order.map((stateCode) => ({
        state: stateCode,
        stateName: stateMap[stateCode] || stateCode,
        names: byState[stateCode],
    }));
};

// Practice-level payer block: the insurance panels the whole practice is credentialing for.
// Selected payers are grouped under their state; every provider inherits them by default.
const PracticePayersCard: React.FC<PracticePayersCardProps> = ({ roster, initialPayers = [], onPayersChange }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [payers, setPayers] = useState<OrgCredentialingPayerRef[]>(initialPayers);
    const [savingLoading, setSavingLoading] = useState(false);
    const [rosterStates, setRosterStates] = useState<string[]>([]);
    const [stateList] = useGetSettingData(EnumFields.STATE);
    const [credentialInfo] = useCredentialInfo();

    // Sync selected payers when the persisted enrollment payers arrive (async fetch).
    // Guard on length so a fresh `[]` default from the parent can't clobber a local selection.
    useEffect(() => {
        if (initialPayers.length) {
            setPayers(initialPayers);
        }
    }, [initialPayers]);

    useEffect(() => {
        onPayersChange?.(payers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payers]);

    // Fetch the states used to filter the payer picker. With a roster, aggregate the states
    // across those providers. When the roster is empty (e.g. the standalone provider flow), fall
    // back to the current provider's own states from the credentialing overview.
    useEffect(() => {
        const fetchStates = async () => {
            if (roster.length === 0) {
                setRosterStates(credentialInfo.states || []);
                return;
            }
            try {
                const providerIds = roster.map((p) => p.providerId);
                const res = await getCredentialingProviderStates(providerIds);
                const list = res?.data?.data;
                if (Array.isArray(list)) {
                    setRosterStates(list);
                }
            } catch (err) {
                console.error('Failed to fetch provider states:', err);
            }
        };
        fetchStates();
    }, [roster, credentialInfo.states]);

    // Build state code to name mapping
    const stateCodeMap = useMemo(() => {
        const map: Record<string, string> = {};
        (stateList as ServerMeta[])?.forEach((state) => {
            map[state.dictKey] = state.content;
        });
        return map;
    }, [stateList]);

    const handlePayersSave = async (next: OrgCredentialingPayerRef[]) => {
        setSavingLoading(true);
        try {
            const res = await saveGroupCredentialingEnrollmentPayers(next.map((p) => p.klarityPayerId));
            if (res && !res.error) {
                setPayers(next);
                setModalOpen(false);
                message.success('Payers saved');
            } else {
                message.error(res?.error || 'Failed to save payers');
            }
        } finally {
            setSavingLoading(false);
        }
    };

    return (
        <div className={s.card}>
            <div className={s.cardTitle}>Practice payers</div>
            <div className={s.cardDesc}>
                The insurance panels your whole practice is credentialing for. Every provider you add inherits these by
                default, and can adjust them individually.
            </div>

            {payers.length ? (
                groupByState(payers, stateCodeMap).map(({ state, stateName, names }) => (
                    <div key={state} className={s.stateGroup}>
                        <span className={s.stateLabel}>{stateName}</span>
                        <div className={s.payerChips}>
                            {names.map((name) => (
                                <span key={name} className={s.payerChip}>
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                ))
            ) : (
                <div className={s.payerEmpty}>No payers selected.</div>
            )}

            <Button className={s.selectPayersBtn} onClick={() => setModalOpen(true)} disabled={savingLoading}>
                {payers.length ? 'Edit payers' : 'Select payers'}
            </Button>

            <PayerPickerModal
                open={modalOpen}
                states={rosterStates}
                value={payers}
                onCancel={() => setModalOpen(false)}
                onSave={handlePayersSave}
                stateCodeMap={stateCodeMap}
                rosterSize={roster.length}
            />
        </div>
    );
};

export default PracticePayersCard;
